import { type KeyboardEvent } from 'react'
import { type CrossClientQueryClientQueryMode, CrossClientQueryClientQueryModes, type CrossClientQueryDataSource } from '../../models/CrossClient'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { type User } from '../../models/User'
import { type IntegrationServiceId } from '../../common/integrations'
import { getCrossClientIntegrationDisplayName } from './integrationDisplay'

// Make a `div role="button"` keyboard-activatable: runs the handler on Enter or Space keydown. (Native
// buttons fire Space on keyup; this fires both on keydown, which is fine for these click-only rows.)
export const onActivateKeyDown =
  (handler: () => void) =>
  (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handler()
    }
  }

export const CLIENT_MODE_LABEL: Record<CrossClientQueryClientQueryMode, string> = {
  [CrossClientQueryClientQueryModes.ONLY_OWNED]: 'Yours only',
  [CrossClientQueryClientQueryModes.WITH_SHARED]: 'Yours + Shared',
  [CrossClientQueryClientQueryModes.ALL_AVAILABLE]: 'All available',
}

export const getIntegrationSourceDisplayNames = (sources: CrossClientQueryDataSource[]): string[] =>
  sources
    .filter((source) => source.type === 'integration')
    .map((source) => getCrossClientIntegrationDisplayName(source.integrationType as IntegrationServiceId))

export const pluralize = (count: number, singular: string, plural: string = `${singular}s`): string => (count === 1 ? singular : plural)

export const joinNames = (names: string[]): string => {
  if (names.length === 0) {
    return ''
  }
  if (names.length === 1) {
    return names[0]
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

export const getOwnerLabel = (currentUser: User, teammates: Record<string, User>, ownerId: string | null) => {
  if (!ownerId) {
    return '-'
  }
  if (ownerId === currentUser.id) {
    return `${getUserDisplayName(currentUser)} (You)`
  }
  const owner = teammates[ownerId]
  return owner ? getUserDisplayName(owner) : '-'
}
